import {
  CompleteLifeReviewSessionPayload,
  LifeReview,
  LifeReviewPrompt,
  LifeReviewPromptSection,
  useCompleteMyLifeReviewSessionMutation,
  useDeleteMyLifeReviewResponseMutation,
  useGetMyLifeReviewPromptsQuery,
  useGetMyLifeReviewQuery
} from 'api/LifeReviewApi';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { selectAuthUser } from 'store/authSlice';
import { LIFE_REVIEW_DRAFT_STORAGE_KEY_PREFIX } from 'store/securityTeardown';
import { getLifeStoryResponseText } from 'utils/lifeStory';

const SAVE_MAX_ATTEMPTS = 3;
const SAVE_BACKOFF_DELAYS_MS = [0, 500, 1000];
const LIFE_REVIEW_DRAFT_TTL_MS = 12 * 60 * 60 * 1000;

type LifeReviewDraftStoragePayload = {
  draftByPromptId: Record<string, string>;
  expiresAtMs: number;
};

export const createLifeReviewDraftStoragePayload = (
  draftByPromptId: Record<string, string>,
  nowMs = Date.now()
): LifeReviewDraftStoragePayload => ({
  draftByPromptId,
  expiresAtMs: nowMs + LIFE_REVIEW_DRAFT_TTL_MS
});

export const readLifeReviewDraftStoragePayload = (raw: string | null, nowMs = Date.now()): Record<string, string> => {
  if (!raw) {
    return {};
  }

  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') {
      return {};
    }

    if ('expiresAtMs' in parsed && 'draftByPromptId' in parsed) {
      if (typeof parsed.expiresAtMs !== 'number' || parsed.expiresAtMs <= nowMs) {
        return {};
      }

      return typeof parsed.draftByPromptId === 'object' && parsed.draftByPromptId ? parsed.draftByPromptId : {};
    }

    return typeof parsed === 'object' && parsed ? parsed : {};
  } catch {
    return {};
  }
};

type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';
type ResponseState = 'unanswered' | 'intentionallyBlank' | 'answered';

export type PersistedLifeReviewResponse = {
  id?: string;
  promptId?: string;
  promptTemplateId?: string;
  displayText?: string;
  durationSec?: number;
  hasAudio?: boolean;
  transcriptionStatus?: string;
  transcriptionError?: string;
  audioUrl?: string;
  audioDownloadUrl?: string;
  audioStorageKey?: string;
  responseText?: string | null;
  transcriptText?: string | null;
  editedTranscriptText?: string | null;
};

export const getResponseState = (value: string | null | undefined, hasDraft: boolean): ResponseState => {
  if ((value || '').trim()) {
    return 'answered';
  }

  return hasDraft ? 'intentionallyBlank' : 'unanswered';
};

const getResponseText = (
  response?: {
    responseText?: string | null;
    text?: string | null;
    displayText?: string | null;
    editedTranscriptText?: string | null;
    transcriptText?: string | null;
  } | null
) => getLifeStoryResponseText(response);

const isResponseCompleted = (response?: PersistedLifeReviewResponse | null) => {
  const text = getResponseText(response);
  if (text.trim()) {
    return true;
  }

  return Boolean(response?.hasAudio || response?.audioStorageKey);
};

const getResponsePromptId = (response?: PersistedLifeReviewResponse | null) => response?.promptId || response?.promptTemplateId;

const createPersistedResponseMap = (lifeReview?: LifeReview | null) => {
  const persistedByPromptId: Record<string, string> = {};

  for (const section of lifeReview?.sections || []) {
    for (const prompt of section.prompts || []) {
      if (!prompt?.id) {
        continue;
      }

      const response = prompt.response || prompt.responses?.[0];
      const text = getResponseText(response);
      if (text) {
        persistedByPromptId[prompt.id] = text;
      }
    }
  }

  for (const response of lifeReview?.responses || []) {
    const responsePromptId = getResponsePromptId(response);
    if (!responsePromptId) {
      continue;
    }

    const text = getResponseText(response);
    if (text) {
      persistedByPromptId[responsePromptId] = text;
    }
  }

  return persistedByPromptId;
};

const createPersistedCompletionMap = (lifeReview?: LifeReview | null) => {
  const completedByPromptId: Record<string, boolean> = {};

  for (const section of lifeReview?.sections || []) {
    for (const prompt of section.prompts || []) {
      if (!prompt?.id) {
        continue;
      }

      const response = prompt.response || prompt.responses?.[0];
      if (isResponseCompleted(response)) {
        completedByPromptId[prompt.id] = true;
      }
    }
  }

  for (const response of lifeReview?.responses || []) {
    const responsePromptId = getResponsePromptId(response);
    if (!responsePromptId) {
      continue;
    }

    if (isResponseCompleted(response)) {
      completedByPromptId[responsePromptId] = true;
    }
  }

  return completedByPromptId;
};

const createPersistedResponseIdMap = (lifeReview?: LifeReview | null) => {
  const responseIdByPromptId: Record<string, string> = {};

  for (const section of lifeReview?.sections || []) {
    for (const prompt of section.prompts || []) {
      if (!prompt?.id) {
        continue;
      }

      const response = prompt.response || prompt.responses?.[0];
      if (response?.id) {
        responseIdByPromptId[prompt.id] = response.id;
      }
    }
  }

  for (const response of lifeReview?.responses || []) {
    const responsePromptId = getResponsePromptId(response);
    if (responsePromptId && response.id) {
      responseIdByPromptId[responsePromptId] = response.id;
    }
  }

  return responseIdByPromptId;
};

const createPersistedResponseByPromptIdMap = (lifeReview?: LifeReview | null) => {
  const responseByPromptId: Record<string, PersistedLifeReviewResponse> = {};

  const mergeResponse = (promptId: string, response: PersistedLifeReviewResponse) => {
    const existingResponse = responseByPromptId[promptId];
    responseByPromptId[promptId] = {
      ...existingResponse,
      ...response,
      promptId,
      promptTemplateId: response.promptTemplateId || existingResponse?.promptTemplateId,
      hasAudio: Boolean(response.hasAudio || existingResponse?.hasAudio || response.audioStorageKey || existingResponse?.audioStorageKey),
      audioUrl: response.audioUrl || existingResponse?.audioUrl,
      audioDownloadUrl: response.audioDownloadUrl || existingResponse?.audioDownloadUrl,
      audioStorageKey: response.audioStorageKey || existingResponse?.audioStorageKey,
      durationSec: response.durationSec ?? existingResponse?.durationSec,
      transcriptionStatus: response.transcriptionStatus || existingResponse?.transcriptionStatus,
      transcriptionError: response.transcriptionError || existingResponse?.transcriptionError,
      displayText: response.displayText ?? existingResponse?.displayText,
      responseText: response.responseText ?? existingResponse?.responseText,
      transcriptText: response.transcriptText ?? existingResponse?.transcriptText,
      editedTranscriptText: response.editedTranscriptText ?? existingResponse?.editedTranscriptText
    };
  };

  for (const section of lifeReview?.sections || []) {
    for (const prompt of section.prompts || []) {
      if (!prompt?.id) {
        continue;
      }

      const response = prompt.response || prompt.responses?.[0];
      if (!response) {
        continue;
      }

      mergeResponse(prompt.id, {
        ...response,
        promptId: response.promptId || prompt.id,
        promptTemplateId: response.promptTemplateId || prompt.id
      });
    }
  }

  for (const response of lifeReview?.responses || []) {
    const responsePromptId = response.promptId || response.promptTemplateId;
    if (!responsePromptId) {
      continue;
    }

    mergeResponse(responsePromptId, {
      ...response,
      promptId: responsePromptId,
      promptTemplateId: response.promptTemplateId || responsePromptId
    });
  }

  return responseByPromptId;
};

export const getPromptDisplayText = (prompt?: LifeReviewPrompt) => prompt?.prompt || prompt?.promptText || prompt?.question || 'Untitled prompt';

export const getFlattenedPrompts = (sections?: LifeReviewPromptSection[]) =>
  (sections || []).flatMap((section, sectionIndex) =>
    (section.prompts || []).map((prompt, promptIndex) => ({
      prompt,
      sectionIndex,
      promptIndex
    }))
  );

export const getFirstIncompletePromptId = (sections: LifeReviewPromptSection[] | undefined, completedByPromptId: Record<string, boolean>) => {
  const flattenedPrompts = getFlattenedPrompts(sections);
  return flattenedPrompts.find(({ prompt }) => !completedByPromptId[prompt.id])?.prompt.id || flattenedPrompts[0]?.prompt.id;
};

export function useLifeReviewSessionState(initialPromptId?: string) {
  const authUser = useSelector(selectAuthUser);
  const { data: lifeReview, isLoading: isLoadingLifeReview, refetch: refetchLifeReview } = useGetMyLifeReviewQuery();
  const { data: sections, isLoading: isLoadingPrompts } = useGetMyLifeReviewPromptsQuery();
  const [completeSession] = useCompleteMyLifeReviewSessionMutation();
  const [deleteResponse] = useDeleteMyLifeReviewResponseMutation();
  const [saveError, setSaveError] = useState('');
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  const draftStorageKey = useMemo(() => {
    const tenantNamespace = authUser?.tenantOrganizationId || 'no-tenant';
    const userNamespace = authUser?.id || 'anonymous';
    return `${LIFE_REVIEW_DRAFT_STORAGE_KEY_PREFIX}:${tenantNamespace}:${userNamespace}`;
  }, [authUser?.id, authUser?.tenantOrganizationId]);
  const loadedDraftStorageKeyRef = useRef<string | null>(null);
  const [draftByPromptId, setDraftByPromptId] = useState<Record<string, string>>(() => {
    return {};
  });

  useEffect(() => {
    if (typeof window === 'undefined') {
      loadedDraftStorageKeyRef.current = draftStorageKey;
      return;
    }

    try {
      const raw = window.sessionStorage.getItem(draftStorageKey) || window.localStorage.getItem(draftStorageKey);
      const parsedDraftByPromptId = readLifeReviewDraftStoragePayload(raw);
      setDraftByPromptId(parsedDraftByPromptId);

      if (Object.keys(parsedDraftByPromptId).length > 0) {
        const payload = createLifeReviewDraftStoragePayload(parsedDraftByPromptId);
        window.sessionStorage.setItem(draftStorageKey, JSON.stringify(payload));
      }

      window.localStorage.removeItem(draftStorageKey);
      loadedDraftStorageKeyRef.current = draftStorageKey;
    } catch {
      setDraftByPromptId({});
      loadedDraftStorageKeyRef.current = draftStorageKey;
    }
  }, [draftStorageKey]);

  const persistedByPromptId = useMemo(() => createPersistedResponseMap(lifeReview), [lifeReview]);
  const persistedCompletedByPromptId = useMemo(() => createPersistedCompletionMap(lifeReview), [lifeReview]);
  const persistedResponseIdByPromptId = useMemo(() => createPersistedResponseIdMap(lifeReview), [lifeReview]);
  const persistedResponseByPromptId = useMemo(() => createPersistedResponseByPromptIdMap(lifeReview), [lifeReview]);
  const flattenedPrompts = useMemo(() => getFlattenedPrompts(sections), [sections]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    if (loadedDraftStorageKeyRef.current !== draftStorageKey) {
      return;
    }

    const payload = createLifeReviewDraftStoragePayload(draftByPromptId);
    window.sessionStorage.setItem(draftStorageKey, JSON.stringify(payload));
  }, [draftByPromptId, draftStorageKey]);

  const [selectedPromptId, setSelectedPromptId] = useState('');

  const effectiveSelectedPromptId =
    selectedPromptId ||
    (initialPromptId && flattenedPrompts.some(({ prompt }) => prompt.id === initialPromptId) ? initialPromptId : '') ||
    getFirstIncompletePromptId(sections, persistedCompletedByPromptId) ||
    '';

  const selectedPromptMeta = flattenedPrompts.find(({ prompt }) => prompt.id === effectiveSelectedPromptId);
  const selectedPrompt = selectedPromptMeta?.prompt;
  const selectedSectionIndex = selectedPromptMeta?.sectionIndex || 0;
  const selectedPromptIndex = flattenedPrompts.findIndex(({ prompt }) => prompt.id === effectiveSelectedPromptId);

  const totalPromptCount = flattenedPrompts.length;
  const completedPromptCount = flattenedPrompts.reduce(
    (count, { prompt }) => count + (persistedCompletedByPromptId[prompt.id] ? 1 : 0),
    0
  );

  const currentPromptValue = selectedPrompt ? (draftByPromptId[selectedPrompt.id] ?? persistedByPromptId[selectedPrompt.id] ?? '') : '';

  const setCurrentPromptValue = useCallback(
    (value: string) => {
      if (!selectedPrompt?.id) {
        return;
      }

      setDraftByPromptId((prev) => ({
        ...prev,
        [selectedPrompt.id]: value
      }));

      if (saveStatus !== 'saving') {
        setSaveStatus('idle');
      }
    },
    [saveStatus, selectedPrompt?.id]
  );

  const goToPrompt = useCallback((promptId: string) => {
    setSelectedPromptId(promptId);
  }, []);

  const movePrompt = useCallback(
    (direction: -1 | 1) => {
      if (!effectiveSelectedPromptId) {
        return;
      }

      const currentIndex = flattenedPrompts.findIndex(({ prompt }) => prompt.id === effectiveSelectedPromptId);
      if (currentIndex < 0) {
        return;
      }

      const nextPrompt = flattenedPrompts[currentIndex + direction]?.prompt;
      if (nextPrompt?.id) {
        setSelectedPromptId(nextPrompt.id);
      }
    },
    [effectiveSelectedPromptId, flattenedPrompts]
  );

  const delay = useCallback(async (ms: number) => {
    await new Promise<void>((resolve) => {
      setTimeout(() => resolve(), ms);
    });
  }, []);

  const tryCompleteSessionWithRetry = useCallback(
    async (responses: CompleteLifeReviewSessionPayload['responses']) => {
      for (let attempt = 0; attempt < SAVE_MAX_ATTEMPTS; attempt += 1) {
        try {
          if (attempt > 0) {
            await delay(SAVE_BACKOFF_DELAYS_MS[attempt] || SAVE_BACKOFF_DELAYS_MS[SAVE_BACKOFF_DELAYS_MS.length - 1]);
          }

          await completeSession({ responses }).unwrap();
          return;
        } catch (error) {
          if (attempt === SAVE_MAX_ATTEMPTS - 1) {
            throw error;
          }
        }
      }
    },
    [completeSession, delay]
  );

  const tryDeleteResponseWithRetry = useCallback(
    async (responseId: string) => {
      for (let attempt = 0; attempt < SAVE_MAX_ATTEMPTS; attempt += 1) {
        try {
          if (attempt > 0) {
            await delay(SAVE_BACKOFF_DELAYS_MS[attempt] || SAVE_BACKOFF_DELAYS_MS[SAVE_BACKOFF_DELAYS_MS.length - 1]);
          }

          await deleteResponse({ responseId }).unwrap();
          return;
        } catch (error) {
          if (attempt === SAVE_MAX_ATTEMPTS - 1) {
            throw error;
          }
        }
      }
    },
    [delay, deleteResponse]
  );

  const savePromptIds = useCallback(
    async (promptIds: string[], audioStorageKeyByPromptId?: Record<string, string>) => {
      const responseByPromptId = promptIds.reduce<Record<string, { responseText?: string; audioStorageKey?: string }>>((acc, promptId) => {
        const hasLocalDraft = Object.prototype.hasOwnProperty.call(draftByPromptId, promptId);
        const rawValue = draftByPromptId[promptId] ?? persistedByPromptId[promptId] ?? '';
        const responseState = getResponseState(rawValue, hasLocalDraft);

        const responseText = rawValue.trim();
        const audioStorageKey = audioStorageKeyByPromptId?.[promptId];

        if (responseState === 'answered' || audioStorageKey) {
          acc[promptId] = {
            responseText: responseText || undefined,
            audioStorageKey
          };
        }

        return acc;
      }, {});

      const promptIdsToDelete = promptIds.filter((promptId) => {
        if (audioStorageKeyByPromptId?.[promptId]) {
          return false;
        }

        const hasLocalDraft = Object.prototype.hasOwnProperty.call(draftByPromptId, promptId);
        const rawValue = draftByPromptId[promptId] ?? persistedByPromptId[promptId] ?? '';
        const responseState = getResponseState(rawValue, hasLocalDraft);

        return responseState === 'intentionallyBlank' && Boolean(persistedResponseIdByPromptId[promptId]);
      });

      const responses = Object.entries(responseByPromptId)
        .map(([promptId, response]) => ({
          promptTemplateId: promptId,
          responseText: response.responseText,
          audioStorageKey: response.audioStorageKey
        }))
        .filter((response) => Boolean(response.responseText || response.audioStorageKey));

      if (!responses.length && !promptIdsToDelete.length) {
        setSaveStatus('idle');
        return true;
      }

      try {
        setSaveError('');
        setSaveStatus('saving');
        if (responses.length) {
          await tryCompleteSessionWithRetry(responses);
        }

        if (promptIdsToDelete.length) {
          await Promise.all(
            promptIdsToDelete.map(async (promptId) => {
              const responseId = persistedResponseIdByPromptId[promptId];
              if (responseId) {
                await tryDeleteResponseWithRetry(responseId);
              }
            })
          );
        }

        const refreshedLifeReview = await refetchLifeReview().unwrap();
        const refreshedPersistedMap = createPersistedResponseMap(refreshedLifeReview);
        const refreshedResponseIdMap = createPersistedResponseIdMap(refreshedLifeReview);

        const successfulPromptIds = responses
          .filter((response) => {
            if (response.responseText) {
              return refreshedPersistedMap[response.promptTemplateId]?.trim() === response.responseText;
            }

            return Boolean(refreshedResponseIdMap[response.promptTemplateId]);
          })
          .map((response) => response.promptTemplateId);

        const successfulDeletedPromptIds = promptIdsToDelete.filter((promptId) => !refreshedPersistedMap[promptId]?.trim());

        const successfullySyncedPromptIds = Array.from(new Set([...successfulPromptIds, ...successfulDeletedPromptIds]));

        if (successfullySyncedPromptIds.length) {
          setDraftByPromptId((prev) => {
            const next = { ...prev };
            for (const promptId of successfullySyncedPromptIds) {
              const hasLocalDraft = Object.prototype.hasOwnProperty.call(next, promptId);
              const nextResponseState = getResponseState(next[promptId], hasLocalDraft);
              if ((next[promptId] ?? '').trim() === (responseByPromptId[promptId]?.responseText || '') || nextResponseState === 'intentionallyBlank') {
                delete next[promptId];
              }
            }

            return next;
          });
        }

        setSaveStatus('saved');
        return true;
      } catch (error: any) {
        setSaveStatus('error');
        setSaveError(error?.data?.message || 'Unable to save your response right now. Please try again.');
        return false;
      }
    },
    [
      draftByPromptId,
      persistedByPromptId,
      persistedResponseIdByPromptId,
      refetchLifeReview,
      tryCompleteSessionWithRetry,
      tryDeleteResponseWithRetry
    ]
  );

  const saveCurrentPrompt = useCallback(async () => {
    if (!selectedPrompt?.id) {
      return false;
    }

    return savePromptIds([selectedPrompt.id]);
  }, [savePromptIds, selectedPrompt?.id]);

  const saveAllDrafts = useCallback(async () => {
    const promptIds = Array.from(new Set([...Object.keys(draftByPromptId), ...Object.keys(persistedByPromptId)]));
    return savePromptIds(promptIds);
  }, [draftByPromptId, persistedByPromptId, savePromptIds]);

  const savePromptAudio = useCallback(
    async (promptId: string, audioStorageKey: string) => {
      return savePromptIds([promptId], { [promptId]: audioStorageKey });
    },
    [savePromptIds]
  );

  return {
    sections: sections || [],
    flattenedPrompts,
    isLoading: isLoadingLifeReview || isLoadingPrompts,
    isSaving: saveStatus === 'saving',
    saveStatus,
    saveError,
    selectedPrompt,
    selectedPromptId: effectiveSelectedPromptId,
    selectedSectionIndex,
    selectedPromptIndex,
    totalPromptCount,
    completedPromptCount,
    draftByPromptId,
    persistedByPromptId,
    persistedCompletedByPromptId,
    persistedResponseIdByPromptId,
    persistedResponseByPromptId,
    currentPromptValue,
    setCurrentPromptValue,
    goToPrompt,
    movePrompt,
    saveCurrentPrompt,
    saveAllDrafts,
    savePromptAudio,
    refreshLifeReview: refetchLifeReview
  };
}
